import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  PhoneIncoming, ListTree, Users, Headset, ShieldCheck, Check, X,
  Phone, Building2, Delete,
} from "lucide-react";

const Frame = ({ label, right, children, testid }) => (
  <div className="bg-[#0a0a0a] text-white border border-black shadow-[0_30px_80px_-20px_rgba(0,0,0,0.4)]" data-testid={testid}>
    <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/10">
      <span className="font-mono-t text-[11px] uppercase tracking-widest text-neutral-300 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-[#0047FF] animate-pulse" /> {label}
      </span>
      {right && <span className="font-mono-t text-[11px] text-neutral-500">{right}</span>}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

/* ---------------- Multi-tenant console ---------------- */
const tenants = [
  { name: "Acme Corp", ext: 24 },
  { name: "Globex Ltd", ext: 12 },
  { name: "Umbrella Inc", ext: 48 },
  { name: "Initech", ext: 8 },
];
export const MultiTenantVisual = () => {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setActive((a) => (a + 1) % tenants.length), 1600);
    return () => clearInterval(id);
  }, []);
  return (
    <Frame label="OwnPBX Console" right="4 tenants · isolated" testid="pbx-visual-multitenant">
      <div className="space-y-2 min-h-[300px]">
        {tenants.map((t, i) => (
          <motion.div
            key={t.name}
            animate={{
              borderColor: i === active ? "#0047FF" : "rgba(255,255,255,0.1)",
              backgroundColor: i === active ? "rgba(0,71,255,0.08)" : "rgba(255,255,255,0.02)",
            }}
            transition={{ duration: 0.4 }}
            className="flex items-center justify-between border-l-2 px-4 py-3.5"
          >
            <div className="flex items-center gap-3">
              <span className={`grid place-items-center w-8 h-8 ${i === active ? "bg-[#0047FF] text-white" : "bg-white/10 text-neutral-400"}`}>
                <Building2 size={15} />
              </span>
              <div>
                <p className="text-sm font-semibold">{t.name}</p>
                <p className="font-mono-t text-[10px] text-neutral-500">tenant-{String(i + 1).padStart(3, "0")}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-mono-t text-sm">{t.ext}</p>
              <p className="font-mono-t text-[9px] uppercase tracking-widest text-neutral-500">ext.</p>
            </div>
          </motion.div>
        ))}
      </div>
    </Frame>
  );
};

/* ---------------- Call-flow routing ---------------- */
const flow = [
  { icon: PhoneIncoming, label: "Incoming call", sub: "+91 80 4718 2210" },
  { icon: ListTree, label: "IVR menu", sub: "Press 1 for Sales" },
  { icon: Users, label: "Ring group / queue", sub: "Sales · 3 agents" },
  { icon: Headset, label: "Agent connected", sub: "Priya · ext. 104" },
];
export const CallFlowVisual = () => {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setStep((s) => (s + 1) % (flow.length + 1)), 1200);
    return () => clearInterval(id);
  }, []);
  return (
    <Frame label="Call routing" right="live flow" testid="pbx-visual-callflow">
      <div className="min-h-[300px] flex flex-col justify-center gap-0">
        {flow.map((f, i) => {
          const done = i < step;
          const isActive = i === step - 1;
          return (
            <div key={f.label}>
              <motion.div
                animate={{
                  borderColor: done ? "#0047FF" : "rgba(255,255,255,0.12)",
                  opacity: done ? 1 : 0.4,
                }}
                transition={{ duration: 0.35 }}
                className="flex items-center gap-3 border px-4 py-3"
              >
                <span className={`grid place-items-center w-9 h-9 ${done ? "bg-[#0047FF] text-white" : "bg-white/5 text-neutral-500"}`}>
                  <f.icon size={16} />
                </span>
                <div className="flex-1">
                  <p className="text-sm font-semibold">{f.label}</p>
                  <p className="font-mono-t text-[10px] text-neutral-500">{f.sub}</p>
                </div>
                {isActive && (
                  <motion.span
                    className="w-2 h-2 rounded-full bg-[#0047FF]"
                    animate={{ scale: [1, 1.6, 1], opacity: [1, 0.4, 1] }}
                    transition={{ duration: 0.8, repeat: Infinity }}
                  />
                )}
              </motion.div>
              {i < flow.length - 1 && (
                <div className="ml-[26px] h-4 w-px bg-white/10 relative overflow-hidden">
                  {i < step - 1 && <span className="absolute inset-0 bg-[#0047FF]" />}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Frame>
  );
};

/* ---------------- Softphone dialer ---------------- */
const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];
const target = "80471822";
export const SoftphoneVisual = () => {
  const [typed, setTyped] = useState("");
  const [calling, setCalling] = useState(false);
  useEffect(() => {
    let i = 0;
    let calling = false;
    const id = setInterval(() => {
      if (calling) return;
      if (i < target.length) {
        i += 1;
        setTyped(target.slice(0, i));
      } else {
        calling = true;
        setCalling(true);
        setTimeout(() => {
          setCalling(false);
          setTyped("");
          i = 0;
          calling = false;
        }, 1600);
      }
    }, 320);
    return () => clearInterval(id);
  }, []);
  const activeKey = typed.slice(-1);
  return (
    <Frame label="Browser softphone" right="no desk phone" testid="pbx-visual-softphone">
      <div className="min-h-[300px] flex flex-col">
        <div className="text-center py-4">
          <p className="font-mono-t text-2xl tracking-widest min-h-[32px]">
            {calling ? <span className="text-[#0047FF]">Calling…</span> : (typed || <span className="text-neutral-600">+91 …</span>)}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2 flex-1">
          {keys.map((k) => (
            <motion.div
              key={k}
              animate={{
                backgroundColor: activeKey === k && !calling ? "#0047FF" : "rgba(255,255,255,0.04)",
                color: activeKey === k && !calling ? "#fff" : "rgba(255,255,255,0.7)",
              }}
              transition={{ duration: 0.15 }}
              className="grid place-items-center py-3 font-heading text-lg border border-white/10"
            >
              {k}
            </motion.div>
          ))}
        </div>
        <div className="flex items-center justify-center gap-3 pt-4">
          <span className="grid place-items-center w-11 h-11 bg-white/5 border border-white/10 text-neutral-400">
            <Delete size={16} />
          </span>
          <motion.span
            className="grid place-items-center w-12 h-12 bg-[#0047FF] text-white rounded-full"
            animate={calling ? { scale: [1, 1.08, 1] } : { scale: 1 }}
            transition={{ duration: 0.7, repeat: calling ? Infinity : 0 }}
          >
            <Phone size={18} />
          </motion.span>
        </div>
      </div>
    </Frame>
  );
};

/* ---------------- Security log ---------------- */
const events = [
  { ip: "203.0.113.24", status: "allow", note: "Whitelisted" },
  { ip: "45.148.10.91", status: "block", note: "Not in allowlist" },
  { ip: "198.51.100.7", status: "allow", note: "Admin login" },
  { ip: "185.220.101.4", status: "block", note: "Brute-force blocked" },
  { ip: "203.0.113.25", status: "allow", note: "Agent login" },
];
export const SecurityVisual = () => {
  const [count, setCount] = useState(1);
  useEffect(() => {
    const id = setInterval(() => setCount((c) => (c >= events.length ? 1 : c + 1)), 1400);
    return () => clearInterval(id);
  }, []);
  const shown = events.slice(0, count);
  return (
    <Frame label="Access control" right="IP whitelisting" testid="pbx-visual-security">
      <div className="min-h-[300px] flex flex-col">
        <div className="flex items-center gap-3 pb-4 mb-2 border-b border-white/10">
          <span className="relative grid place-items-center w-10 h-10 bg-[#0047FF]/10 border border-[#0047FF]/40">
            <ShieldCheck size={18} className="text-[#0047FF]" />
          </span>
          <div>
            <p className="text-sm font-semibold">Firewall active</p>
            <p className="font-mono-t text-[10px] text-neutral-500">Role-based access · login protection</p>
          </div>
        </div>
        <div className="space-y-2 font-mono-t text-[12px] flex flex-col justify-start">
          <AnimatePresence initial={false}>
            {shown.map((e, i) => (
              <motion.div
                key={`${count}-${i}`}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                className="flex items-center justify-between border-b border-white/5 py-1.5"
              >
                <span className="flex items-center gap-2.5">
                  <span className={`grid place-items-center w-5 h-5 ${e.status === "allow" ? "bg-[#0047FF] text-white" : "bg-white/10 text-neutral-300"}`}>
                    {e.status === "allow" ? <Check size={12} /> : <X size={12} />}
                  </span>
                  <span className={e.status === "block" ? "text-neutral-500 line-through" : "text-neutral-200"}>{e.ip}</span>
                </span>
                <span className={`uppercase tracking-widest text-[9px] ${e.status === "allow" ? "text-[#0047FF]" : "text-neutral-500"}`}>
                  {e.status === "allow" ? "Allowed" : "Blocked"}
                </span>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </Frame>
  );
};

export const PbxVisual = ({ kind }) => {
  if (kind === "multitenant") return <MultiTenantVisual />;
  if (kind === "callflow") return <CallFlowVisual />;
  if (kind === "softphone") return <SoftphoneVisual />;
  if (kind === "security") return <SecurityVisual />;
  return null;
};
